import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCaseStats, useCases } from '../../api/hooks';
import type { DoctorTab } from '../../api/types';
import { useSession } from '../../app/session';
import { PlusIcon } from '../../components/Icons';
import { ErrorState, Loading, TableEmpty } from '../../components/States';
import { Progress, StatusPill } from '../../components/Status';
import { useDebounced } from '../../components/useDebounced';
import { WorksShort } from '../../components/WorksList';
import { fmtDate } from '../../lib/dates';
import { patientName } from '../../lib/patients';

const TABS: DoctorTab[] = ['all', 'action', 'work', 'shipped', 'done'];

export function CasesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgs, orgParam } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (TABS as string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as DoctorTab) : 'all';
  const [query, setQuery] = useState(params.get('q') ?? '');
  const q = useDebounced(query.trim());

  const stats = useCaseStats('clinic', orgParam);
  const cases = useCases({ side: 'clinic', org: orgParam, tab, q: q || undefined });
  const many = orgs.length > 1;

  const setTab = (k: DoctorTab) => {
    const p = new URLSearchParams(params);
    if (k === 'all') p.delete('tab');
    else p.set('tab', k);
    setParams(p, { replace: true });
  };

  const open = (id: string) => navigate(`/cases/${encodeURIComponent(id)}`);
  const rows = cases.data ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('doctor.cases.title')}</h1>
          <p>
            {many ? t('doctor.cases.subAll') : t('doctor.cases.subOne', { name: orgs[0]?.org_name ?? '' })}
          </p>
        </div>
        <Link className="btn btn-primary" to="/new">
          <PlusIcon />
          {t('nav.newCase')}
        </Link>
      </div>

      <div className="tabs" role="tablist">
        {TABS.map((k) => {
          const n = stats.data?.[k] ?? 0;
          return (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
              {t(`doctor.cases.tabs.${k}`)}
              <span className={`n ${k === 'action' && n ? 'hot' : ''}`}>{stats.data ? n : '·'}</span>
            </button>
          );
        })}
      </div>

      <div className="toolbar">
        <input
          className="input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('doctor.cases.search')}
          aria-label={t('doctor.cases.searchAria')}
        />
      </div>

      {cases.isLoading ? (
        <Loading />
      ) : cases.error && !cases.data ? (
        <ErrorState error={cases.error} onRetry={() => void cases.refetch()} />
      ) : !rows.length ? (
        <TableEmpty>
          {q ? t('doctor.cases.notFound') : t('doctor.cases.emptyTab')}
          <br />
          <Link className="btn btn-primary" to="/new">
            {t('doctor.cases.create')}
          </Link>
        </TableEmpty>
      ) : (
        <div className="table-wrap stack">
          <table>
            <thead>
              <tr>
                <th>{t('doctor.cases.cols.patient')}</th>
                <th>{t('doctor.cases.cols.works')}</th>
                <th>{t('doctor.cases.cols.lab')}</th>
                <th>{t('doctor.cases.cols.status')}</th>
                <th>{t('doctor.cases.cols.due')}</th>
                <th>{t('doctor.cases.cols.updated')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr
                  key={c.id}
                  tabIndex={0}
                  onClick={() => open(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') open(c.id);
                  }}
                >
                  <td className="wide">
                    <div className="pname">{patientName(c.patient)}</div>
                    <div className="case-id">
                      {c.number}
                      {many ? `, ${c.clinic.name}` : ''}
                    </div>
                  </td>
                  <td data-label={t('doctor.cases.cols.works')}>
                    <WorksShort items={c.items} />
                  </td>
                  <td className="small" data-label={t('doctor.cases.cols.lab')}>
                    {c.lab.name}
                  </td>
                  <td className="wide">
                    {c.rescan ? (
                      <span className="pill wait">{t('doctor.cases.rescanPill')}</span>
                    ) : (
                      <StatusPill status={c.status} />
                    )}
                    <Progress status={c.status} />
                  </td>
                  <td data-label={t('doctor.cases.cols.due')}>
                    {c.status >= 6 ? <span className="muted">—</span> : fmtDate(c.due_date)}
                  </td>
                  <td className="muted small" data-label={t('doctor.cases.cols.updated')}>
                    {fmtDate(c.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
